import { formatDocSubestipulante, formatVigenciaSubDisplay } from '../hooks';
import type {
  SubEstipulanteCoverageItem,
  SubEstipulanteInfoField,
} from '../types/vida/cadastralMaintenance/subEstipulanteInfoTypes';
import type { ApiCoverageItem, ApiStipulatorItem, ApiUniformCapitalItem } from '../types/vida/contract/contractType';
import type { ListaSubestipulanteItem } from '../types/vida/subEstipulante/subEstipulanteType';
import { formatPhone } from './phoneUtils';
import { formatZipCode } from './zipCodeUtils';

export type SubEstipulanteInfoSections = {
  cadastrais: SubEstipulanteInfoField[];
  contato: SubEstipulanteInfoField[];
  endereco: SubEstipulanteInfoField[];
  comerciais: SubEstipulanteInfoField[];
  coberturas: SubEstipulanteCoverageItem[];
  limitesCapital: SubEstipulanteInfoField[];
  regrasAdesao: SubEstipulanteInfoField[];
  regrasCapital: SubEstipulanteInfoField[];
};

function displayValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const text = String(value).trim();
  return text || '—';
}

function hasTextValue(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
}

function buildField(label: string, value: string, highlight = false): SubEstipulanteInfoField {
  return { label, value, highlight };
}

function buildStreet(endereco?: string, complemento?: string): string {
  const parts = [endereco, complemento].map((part) => part?.trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

function formatTelefone(raw: string | undefined): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '—';
  return formatPhone(digits);
}

function formatCurrencyValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
  if (!Number.isFinite(num)) return displayValue(value);
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
}

function isCoverageActive(item: ApiCoverageItem): boolean {
  const fim = item.dataFimVigenciaCobertura?.trim();
  if (!fim) return true;

  const dateOnly = fim.split(/[Tt\s]/)[0] ?? '';
  const parts = dateOnly.split(/[-/.]/);
  if (parts.length < 3) return true;

  const [a, b, c] = parts;
  const year = a.length === 4 ? Number(a) : Number(c);
  const month = Number(b) - 1;
  const day = a.length === 4 ? Number(c) : Number(a);
  const endDate = new Date(year, month, day);
  endDate.setHours(23, 59, 59, 999);

  return endDate >= new Date();
}

function formatPercentValue(value: string | number | null | undefined): string | undefined {
  if (!hasTextValue(value)) return undefined;
  const text = String(value).trim();
  if (!text) return undefined;
  return text.endsWith('%') ? text : `${text}%`;
}

function buildUniformCapitalMap(items: ApiUniformCapitalItem[]): Map<number, ApiUniformCapitalItem> {
  const map = new Map<number, ApiUniformCapitalItem>();
  items.forEach((item) => {
    map.set(item.identificadorCobertura, item);
  });
  return map;
}

function findUniformCapitalItem(
  item: ApiCoverageItem,
  uniformMap: Map<number, ApiUniformCapitalItem>,
): ApiUniformCapitalItem | undefined {
  return uniformMap.get(item.identCoberturaOut) ?? uniformMap.get(item.identCoberturaApolOut);
}

function resolveCapitalSegurado(
  item: ApiCoverageItem,
  uniform?: ApiUniformCapitalItem,
): string {
  if (hasTextValue(uniform?.valorCapitalUniforme)) {
    return formatCurrencyValue(uniform?.valorCapitalUniforme);
  }
  if (hasTextValue(uniform?.valorLimiteCapital)) {
    return formatCurrencyValue(uniform?.valorLimiteCapital);
  }
  if (hasTextValue(uniform?.valorLimiteFixo)) {
    return formatCurrencyValue(uniform?.valorLimiteFixo);
  }
  if (hasTextValue(item.valorImportSeguradaOut)) {
    return formatCurrencyValue(item.valorImportSeguradaOut);
  }
  if (item.valorLimiteCapital > 0) {
    return formatCurrencyValue(item.valorLimiteCapital);
  }
  if (item.valorCapitalFixo > 0) {
    return formatCurrencyValue(item.valorCapitalFixo);
  }
  return '—';
}

export function mapCoverageItemsToDisplay(
  items: ApiCoverageItem[],
  uniformCapitalItems: ApiUniformCapitalItem[] = [],
): SubEstipulanteCoverageItem[] {
  const uniformMap = buildUniformCapitalMap(uniformCapitalItems);

  return items.map((item) => {
    const uniform = findUniformCapitalItem(item, uniformMap);

    return {
      id: String(item.identCoberturaApolOut ?? item.identCoberturaOut),
      nome: item.nomeCoberturaOut?.trim() || '—',
      capitalSegurado: resolveCapitalSegurado(item, uniform),
      capitalUniforme: hasTextValue(uniform?.valorCapitalUniforme)
        ? formatCurrencyValue(uniform?.valorCapitalUniforme)
        : undefined,
      limiteCapital: hasTextValue(uniform?.valorLimiteCapital)
        ? formatCurrencyValue(uniform?.valorLimiteCapital)
        : undefined,
      limiteFixo: hasTextValue(uniform?.valorLimiteFixo)
        ? formatCurrencyValue(uniform?.valorLimiteFixo)
        : undefined,
      percentualBasica: formatPercentValue(uniform?.percentualCapitalBasica),
      premio: hasTextValue(uniform?.valorPremio)
        ? formatCurrencyValue(uniform?.valorPremio)
        : undefined,
      ativo: isCoverageActive(item),
    };
  });
}

export function mapUniformCapitalToLimites(
  items: ApiUniformCapitalItem[],
): SubEstipulanteInfoField[] {
  if (items.length === 0) {
    return [];
  }

  const fields: SubEstipulanteInfoField[] = [];
  const maxLimiteCapital = items.reduce((max, item) => {
    const value = parseFloat(String(item.valorLimiteCapital).replace(',', '.'));
    return Number.isFinite(value) && value > max ? value : max;
  }, 0);

  if (maxLimiteCapital > 0) {
    fields.push({
      label: 'Capital Segurado Máximo',
      value: formatCurrencyValue(maxLimiteCapital),
    });
  }

  items.forEach((item) => {
    const ramoLabel = item.nomeRamo?.trim()
      || (hasTextValue(item.codigoRamo) ? String(item.codigoRamo).trim() : '');
    const suffix = ramoLabel ? ` (${ramoLabel})` : '';

    if (hasTextValue(item.valorCapitalUniforme)) {
      fields.push({
        label: `Capital Uniforme${suffix}`,
        value: formatCurrencyValue(item.valorCapitalUniforme),
      });
    }

    if (hasTextValue(item.valorLimiteFixo)) {
      fields.push({
        label: `Limite Fixo${suffix}`,
        value: formatCurrencyValue(item.valorLimiteFixo),
      });
    }

    const percentual = formatPercentValue(item.percentualCapitalBasica);
    if (percentual) {
      fields.push({
        label: `Percentual sobre Básica${suffix}`,
        value: percentual,
      });
    }

    if (hasTextValue(item.valorPremio)) {
      fields.push({
        label: `Prêmio${suffix}`,
        value: formatCurrencyValue(item.valorPremio),
      });
    }

    if (hasTextValue(item.quantidade)) {
      fields.push({
        label: `Quantidade${suffix}`,
        value: displayValue(item.quantidade),
      });
    }
  });

  return fields;
}

export type SubEstipulanteAgeLimits = {
  minimumAge?: number;
  maxAgeFirstInvoice?: number;
  maxAgeOtherInvoices?: number;
};

export function mapAgeLimitsToRegrasAdesao(limits: SubEstipulanteAgeLimits): SubEstipulanteInfoField[] {
  const fields: SubEstipulanteInfoField[] = [];

  if (limits.minimumAge != null) {
    fields.push(buildField('Idade Mínima (anos)', displayValue(limits.minimumAge)));
  }

  if (limits.maxAgeFirstInvoice != null) {
    fields.push(buildField('Idade Máxima para 1ª Fatura (anos)', displayValue(limits.maxAgeFirstInvoice)));
  }

  if (limits.maxAgeOtherInvoices != null) {
    fields.push(buildField('Idade Máxima Demais Faturas (anos)', displayValue(limits.maxAgeOtherInvoices)));
  }

  return fields;
}

export function mapUniformCapitalToRegras(
  items: ApiUniformCapitalItem[],
): SubEstipulanteInfoField[] {
  if (items.length === 0) {
    return [];
  }

  const fields: SubEstipulanteInfoField[] = [];

  items.forEach((item) => {
    const ramoLabel = item.nomeRamo?.trim()
      || (hasTextValue(item.codigoRamo) ? String(item.codigoRamo).trim() : '');
    const suffix = ramoLabel ? ` (${ramoLabel})` : '';

    if (hasTextValue(item.valorCapitalUniforme)) {
      fields.push({
        label: `Capital Uniforme${suffix}`,
        value: formatCurrencyValue(item.valorCapitalUniforme),
      });
    }

    if (hasTextValue(item.valorLimiteCapital)) {
      fields.push({
        label: `Limite de Capital${suffix}`,
        value: formatCurrencyValue(item.valorLimiteCapital),
      });
    }

    if (hasTextValue(item.valorLimiteFixo)) {
      fields.push({
        label: `Limite Fixo${suffix}`,
        value: formatCurrencyValue(item.valorLimiteFixo),
      });
    }

    const percentual = formatPercentValue(item.percentualCapitalBasica);
    if (percentual) {
      fields.push({
        label: `Percentual sobre Básica${suffix}`,
        value: percentual,
      });
    }

    if (hasTextValue(item.valorPremio)) {
      fields.push({
        label: `Prêmio${suffix}`,
        value: formatCurrencyValue(item.valorPremio),
      });
    }

    if (hasTextValue(item.quantidade)) {
      fields.push({
        label: `Quantidade${suffix}`,
        value: displayValue(item.quantidade),
      });
    }

    if (item.tipoCapital != null) {
      fields.push({
        label: `Tipo de Capital${suffix}`,
        value: displayValue(item.tipoCapital),
      });
    }
  });

  return fields;
}

export function mapSubEstipulanteInfoData(
  subestipulante: ListaSubestipulanteItem | null,
  estipulante: ApiStipulatorItem | null,
): SubEstipulanteInfoSections {
  const cadastrais: SubEstipulanteInfoField[] = [];
  const contato: SubEstipulanteInfoField[] = [];
  const endereco: SubEstipulanteInfoField[] = [];
  const comerciais: SubEstipulanteInfoField[] = [];

  if (subestipulante) {
    cadastrais.push(
      buildField('Nome', displayValue(subestipulante.nomeSubestipulanteOut)),
      buildField('Documento', formatDocSubestipulante(subestipulante.numDocumentoOut)),
      buildField('Status', displayValue(subestipulante.dsCstatus)),
      buildField('Início da Vigência', formatVigenciaSubDisplay(subestipulante.dataIniVigenciaOut)),
      buildField('Fim da Vigência', formatVigenciaSubDisplay(subestipulante.dataFimVigenciaOut)),
      buildField('Data de Emissão', formatVigenciaSubDisplay(subestipulante.dataEmissaoOut)),
    );
  }

  if (estipulante) {
    cadastrais.push(
      buildField('Razão Social (Estipulante)', displayValue(estipulante.nomeEstipulanteOut)),
      buildField(
        'Documento (Estipulante)',
        formatDocSubestipulante(estipulante.numDocumentoOut),
      ),
      buildField('Início da Vigência (Estipulante)', formatVigenciaSubDisplay(estipulante.dataIniVigenciaOut)),
      buildField('Fim da Vigência (Estipulante)', formatVigenciaSubDisplay(estipulante.dataFimVigenciaOut)),
    );

    contato.push(buildField('Telefone', formatTelefone(estipulante.telefone)));

    const cep = formatZipCode(estipulante.cepEstOut);
    endereco.push(
      buildField('CEP', cep || '—'),
      buildField('Logradouro', buildStreet(estipulante.enderecoEstOut, estipulante.complementoEstOut)),
      buildField('Bairro', displayValue(estipulante.bairroEstOut)),
      buildField('Cidade', displayValue(estipulante.cidadeEstOut)),
      buildField('UF', displayValue(estipulante.ufEstOut)),
    );

    const produto = estipulante.nomeProdutoOut?.trim();
    const codProduto = estipulante.codprodutoOut;
    comerciais.push(
      buildField(
        'Produto',
        produto
          ? `${produto}${codProduto ? ` (${codProduto})` : ''}`
          : displayValue(codProduto),
      ),
      buildField('Nº Apólice', displayValue(estipulante.numApoliceOut)),
      buildField('Nº Proposta', displayValue(estipulante.nroProposta)),
      buildField('Identificador do Contrato', displayValue(estipulante.identContratoOut)),
      buildField('Identificador do Estipulante', displayValue(estipulante.identEstipulanteOut)),
    );
  }

  return {
    cadastrais,
    contato,
    endereco,
    comerciais,
    coberturas: [],
    limitesCapital: [],
    regrasAdesao: [],
    regrasCapital: [],
  };
}

export function buildSubEstipulanteInfoSections(
  subestipulante: ListaSubestipulanteItem | null,
  estipulante: ApiStipulatorItem | null,
  coverageItems: ApiCoverageItem[],
  uniformCapitalItems: ApiUniformCapitalItem[] = [],
  ageLimits: SubEstipulanteAgeLimits = {},
): SubEstipulanteInfoSections {
  const base = mapSubEstipulanteInfoData(subestipulante, estipulante);
  return {
    ...base,
    coberturas: mapCoverageItemsToDisplay(coverageItems, uniformCapitalItems),
    limitesCapital: mapUniformCapitalToLimites(uniformCapitalItems),
    regrasAdesao: mapAgeLimitsToRegrasAdesao(ageLimits),
    regrasCapital: mapUniformCapitalToRegras(uniformCapitalItems),
  };
}
